'use client';
import {
  addToast,
  Button,
  Divider,
  ErrorToast,
  Fieldß,
  Inputß,
  LoadingBar,
  Spinnerß,
} from '@otter-ai/den-components';
import { EyeIcon, EyeOffIcon } from '@otter-ai/den-icons';
import {
  AccessOtherwaysEvent,
  OnboardSignupAttemptEvent,
  Screen,
  trackEvent,
} from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import { validate as isEmail } from 'email-validator';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { referralDecodeQueryCreator } from '@/query/referralDecodeQueryCreator';
import { thirdPartyLoadingAtom } from '@/shared/atoms/thirdPartyLoadingAtom';
import { useCreateUser } from '@/shared/hooks/useCreateUser';
import { usePasswordValidationConfigurations } from '@/shared/hooks/usePasswordValidationConfigurations';
import { referralSignupEnabledAtom } from '../../atoms/referralSignupEnabledAtom';
import { ThirdPartySignInContainer } from '../ThirdPartySignInContainer';
import { Footer } from './Footer';
import { Title } from './Title';

type ReferralFormData = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export function ReferralsMainPage({ token }: { token: string }) {
  const t = useTranslations('referrals');

  const {
    data: referralDecodeData,
    isLoading: isDecodingReferral,
    isError: isReferralDecodeError,
    error: referralDecodeError,
  } = useQuery(referralDecodeQueryCreator({ referralCode: token }));

  const passwordValidationOptions = usePasswordValidationConfigurations();

  const { createUser, isCreatingUser } = useCreateUser({
    screen: Screen.referralSignupNew,
    onError: (error) => {
      addToast(<ErrorToast description={error.message} />);
    },
  });

  const isThirdPartyLoading = useAtomValue(thirdPartyLoadingAtom);

  const containerRef = useRef<HTMLDivElement>(null);

  const [showEmailSignUp, setShowEmailSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ReferralFormData>({
    mode: 'all',
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
    },
  });

  const invitorFirstName = referralDecodeData
    ? referralDecodeData.info.invitor.first_name
    : null;
  const invitorAvatarUrl = referralDecodeData
    ? referralDecodeData.info.invitor.avatar_url
    : null;

  const setReferralSignupEnabled = useSetAtom(referralSignupEnabledAtom);

  useEffect(() => {
    if (isReferralDecodeError) {
      addToast(<ErrorToast description={referralDecodeError.message} />);
      setReferralSignupEnabled(false);
    } else if (referralDecodeData) {
      setReferralSignupEnabled(true);
    }
  }, [
    referralDecodeData,
    isReferralDecodeError,
    referralDecodeError,
    setReferralSignupEnabled,
  ]);

  if (isDecodingReferral || isReferralDecodeError || !invitorFirstName) {
    return (
      <div className="pt-10">
        <Spinnerß />
      </div>
    );
  }

  const onSubmit = (data: ReferralFormData) => {
    trackEvent(
      OnboardSignupAttemptEvent({
        Screen: Screen.referralSignupNew,
        AccountType: 'email',
      }),
    );
    createUser({
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      referralCode: token,
    });
  };

  const isLoading = isCreatingUser || isThirdPartyLoading || isSubmitting;

  const createAccountDisabled =
    !isValid || isCreatingUser || isThirdPartyLoading || isSubmitting;

  return (
    <>
      {isLoading && <LoadingBar data-testid="referrals-loading-bar" />}
      <div className="flex flex-col items-center justify-center space-y-10 min-h-[calc(100vh-112px)]">
        <div className="flex pt-5">
          <div
            ref={containerRef}
            className="flex flex-col sm:rounded-3xl py-6 px-10 sm:bg-palette-gray-50 sm:max-w-[478px] sm:max-h-[670px] w-full sm:overflow-y-auto sm:[box-shadow:0px_10px_10px_-5px_rgba(0,13,51,0.04),0px_20px_25px_-5px_rgba(0,13,51,0.10)]"
          >
            <Title
              invitorFirstName={invitorFirstName}
              invitorAvatarUrl={invitorAvatarUrl}
            />
            <ThirdPartySignInContainer
              referralCode={token}
              canShowAppleOAuth={false}
              canShowGoogleOAuth={true}
              canShowMicrosoftOAuth={true}
              isCreateAccount={true}
              isFullWidthButton={false}
              screen={Screen.referralSignupNew}
              buttonBorderRadius="md"
              buttonContentCentered={true}
            />
            <div className="relative flex items-center w-full pt-3 pb-5">
              <Divider
                height="1px"
                orientation="horizontal"
                width="100%"
                className="bg-palette-gray-600"
              />
              <span className="absolute left-1/2 -translate-x-1/2 px-2 text-sm text-subtle bg-palette-blue-100 sm:bg-palette-gray-50">
                {t('or')}
              </span>
            </div>
            {!showEmailSignUp && (
              <Button
                variant="ghost"
                onClick={() => {
                  trackEvent(
                    AccessOtherwaysEvent({ Screen: Screen.referralSignupNew }),
                  );
                  setShowEmailSignUp(true);
                  setTimeout(() => {
                    if (containerRef.current) {
                      // Scroll container to its bottom.
                      containerRef.current.scrollTo({
                        top: containerRef.current.scrollHeight,
                        behavior: 'smooth',
                      });
                      // Get the bottom position of the container relative to the viewport.
                      const containerBottom =
                        containerRef.current.getBoundingClientRect().bottom;
                      // If the container's bottom is below the viewport, scroll the window.
                      if (containerBottom > window.innerHeight) {
                        window.scrollBy({
                          top: containerBottom - window.innerHeight,
                          behavior: 'smooth',
                        });
                      }
                    }
                  }, 0);
                }}
              >
                {t('other_ways_to_sign_up')}
              </Button>
            )}
            {showEmailSignUp && (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col -space-y-1"
              >
                <Fieldß
                  as="Inputß"
                  error={errors.email?.message ?? ''}
                  label={t('email')}
                >
                  <Inputß
                    as="text"
                    placeholder={t('add_your_work_email')}
                    {...register('email', {
                      required: t('email_is_required'),
                      validate: (value) =>
                        isEmail(value) || t('email_must_be_valid'),
                    })}
                  />
                </Fieldß>
                <Fieldß
                  as="Inputß"
                  error={errors.password?.message ?? ''}
                  label={t('password')}
                  description={t('password_requirements')}
                  className="pb-5"
                >
                  <Inputß
                    as={showPassword ? 'text' : 'password'}
                    placeholder={t('password')}
                    {...register('password', passwordValidationOptions)}
                    appendEnd={
                      showPassword ? (
                        <EyeIcon onClick={() => setShowPassword(false)} />
                      ) : (
                        <EyeOffIcon onClick={() => setShowPassword(true)} />
                      )
                    }
                  />
                </Fieldß>
                <Fieldß
                  as="Inputß"
                  error={errors.firstName?.message ?? ''}
                  label={t('first_name')}
                >
                  <Inputß
                    as="text"
                    placeholder={t('first_name')}
                    {...register('firstName', {
                      required: t('first_name_is_required'),
                    })}
                  />
                </Fieldß>
                <Fieldß
                  as="Inputß"
                  error={errors.lastName?.message ?? ''}
                  label={t('last_name')}
                  className="pb-4"
                >
                  <Inputß
                    as="text"
                    placeholder={t('last_name')}
                    {...register('lastName', {
                      required: t('last_name_is_required'),
                    })}
                  />
                </Fieldß>
                <Button type="submit" disabled={createAccountDisabled}>
                  {t('create_account')}
                </Button>
              </form>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
